import React, { useState, useMemo } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { format, parseISO, isBefore, differenceInDays } from 'date-fns';
import PaymentAutomationSettings from '../components/PaymentAutomationSettings';
import PaymentPulseChart from '../components/PaymentPulseChart';

const { FiCalendar, FiDollarSign, FiSend, FiCheckCircle, FiAlertCircle, FiClock, FiSearch, FiFilter, FiZap, FiSettings, FiArrowRight, FiShield } = FiIcons;

const PaymentReminders = () => {
  const { data, updateRecord, addRecord } = useStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [activeFilter, setActiveFilter] = useState('All');

  const payments = useMemo(() => {
    return (data.rentPayments || []).map(p => {
      const lease = data.leases.find(l => l.id === p.leaseId);
      const property = data.properties.find(prop => prop.id === lease?.propertyId);
      const tenant = data.tenants.find(t => t.id === lease?.tenantId);
      return { ...p, lease, property, tenant };
    }).filter(p => {
      const matchesSearch = p.property?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          p.tenant?.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesFilter = activeFilter === 'All' || p.status === activeFilter;
      return matchesSearch && matchesFilter;
    });
  }, [data, searchTerm, activeFilter]);

  const stats = {
    totalExpected: payments.reduce((sum, p) => sum + p.amount, 0),
    collected: payments.filter(p => p.status === 'Paid').reduce((sum, p) => sum + p.amount, 0),
    overdue: payments.filter(p => p.status === 'Overdue').reduce((sum, p) => sum + p.amount, 0),
    pending: payments.filter(p => p.status === 'Pending').reduce((sum, p) => sum + p.amount, 0)
  };

  const handleMarkPaid = (payment) => {
    const todayStr = format(new Date(), 'yyyy-MM-dd');
    updateRecord('rentPayments', payment.id, { 
      status: 'Paid', 
      paymentDate: todayStr 
    });
    
    addRecord('transactions', {
      propertyId: payment.property.id,
      date: todayStr,
      type: 'Income',
      category: 'Rent',
      amount: payment.amount,
      description: `Rent - ${payment.month} - ${payment.tenant.name}`,
      tags: ['Verified', 'Recurring']
    });
  };

  const handleBatchNudge = () => {
    const overdueOnes = payments.filter(p => p.status === 'Overdue');
    overdueOnes.forEach(p => {
      updateRecord('rentPayments', p.id, { reminderSent: true });
      addRecord('communications', {
        tenantId: p.tenant.id,
        propertyId: p.property.id,
        type: 'SMS',
        date: format(new Date(), 'yyyy-MM-dd'),
        subject: 'Urgent: Rent Overdue',
        notes: 'Automated batch nudge sent for overdue payment.',
        status: 'Sent'
      });
    });
    alert(`Batch nudges sent to ${overdueOnes.length} tenants.`);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Payment Intelligence</h2>
          <p className="text-sm text-gray-500 font-medium italic">Real-time receivables monitoring and collection automation.</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={() => setShowSettings(!showSettings)}
            className={`flex items-center px-5 py-3 rounded-2xl transition-all font-black text-xs uppercase tracking-wider ${showSettings ? 'bg-indigo-600 text-white shadow-lg' : 'bg-white border border-gray-100 text-gray-600 hover:bg-gray-50'}`}
          >
            <SafeIcon icon={FiSettings} className="mr-2" /> Rules
          </button>
          <button 
            onClick={handleBatchNudge}
            className="flex items-center px-5 py-3 bg-red-600 text-white rounded-2xl hover:bg-red-700 transition shadow-lg shadow-red-100 font-black text-xs uppercase tracking-wider"
          >
            <SafeIcon icon={FiZap} className="mr-2" /> Nudge {stats.overdue > 0 ? 'Overdue' : 'All'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <PaymentPulseChart payments={data.rentPayments || []} />
        </div>
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm">
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Portfolio Efficiency</p>
            <div className="flex items-end gap-2">
              <p className="text-4xl font-black text-gray-900">
                {Math.round((stats.collected / (stats.totalExpected || 1)) * 100)}%
              </p>
              <span className="text-[10px] font-bold text-green-600 mb-1 uppercase tracking-tighter">Healthy</span>
            </div>
            <div className="w-full bg-gray-100 h-2 rounded-full mt-4 overflow-hidden">
              <div 
                className="bg-green-500 h-full transition-all duration-1000" 
                style={{ width: `${(stats.collected / (stats.totalExpected || 1)) * 100}%` }} 
              />
            </div>
          </div>
          <div className="bg-red-600 p-6 rounded-3xl text-white shadow-xl relative overflow-hidden group">
            <h3 className="text-[10px] font-black uppercase tracking-widest opacity-80 mb-2">Revenue Leakage</h3>
            <p className="text-4xl font-black">${stats.overdue.toLocaleString()}</p>
            <p className="text-[10px] font-bold uppercase mt-2 text-red-100">Across {payments.filter(p => p.status === 'Overdue').length} units</p>
            <SafeIcon icon={FiAlertCircle} className="absolute -bottom-4 -right-4 text-white/10 group-hover:scale-110 transition-transform" size={80} />
          </div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 space-y-4">
          <div className="flex flex-col md:flex-row gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
            <div className="relative flex-1">
              <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                placeholder="Search tenant or property..." 
                className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="flex bg-gray-100 p-1 rounded-xl">
              {['All', 'Paid', 'Pending', 'Overdue'].map(f => (
                <button
                  key={f}
                  onClick={() => setActiveFilter(f)}
                  className={`px-4 py-1.5 text-[10px] font-black uppercase tracking-widest rounded-lg transition-all ${activeFilter === f ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-50">
            {payments.map(p => (
              <div key={p.id} className="p-6 hover:bg-gray-50/50 transition-all group">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                  <div className="flex items-center gap-4 min-w-[200px]">
                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white shadow-lg ${p.status === 'Paid' ? 'bg-green-500' : p.status === 'Overdue' ? 'bg-red-500' : 'bg-indigo-500'}`}>
                      <SafeIcon icon={p.status === 'Paid' ? FiCheckCircle : p.status === 'Overdue' ? FiAlertCircle : FiClock} size={20} />
                    </div>
                    <div>
                      <h4 className="font-black text-gray-900 leading-tight">{p.property?.name}</h4>
                      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">{p.tenant?.name}</p>
                    </div>
                  </div>

                  <div className="flex-1 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Amount</p>
                      <p className="text-sm font-black text-gray-900">${p.amount.toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Status</p>
                      <span className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full ${p.status === 'Paid' ? 'bg-green-50 text-green-600' : p.status === 'Overdue' ? 'bg-red-50 text-red-600' : 'bg-indigo-50 text-indigo-600'}`}>
                        {p.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Due Date</p>
                      <p className="text-xs font-bold text-gray-700">{format(parseISO(p.dueDate), 'MMM dd')}</p>
                    </div>
                    <div className="hidden lg:block">
                      <p className="text-[9px] font-black text-gray-400 uppercase mb-1">Alerts</p>
                      <div className="flex gap-1">
                        {p.reminderSent && <SafeIcon icon={FiSend} size={12} className="text-indigo-400" />}
                        {p.status === 'Overdue' && <SafeIcon icon={FiAlertCircle} size={12} className="text-red-400" />}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 justify-end">
                    {p.status !== 'Paid' ? (
                      <button 
                        onClick={() => handleMarkPaid(p)}
                        className="px-6 py-3 bg-gray-900 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-black transition shadow-lg flex items-center gap-2"
                      >
                        Log Payment <SafeIcon icon={FiArrowRight} />
                      </button>
                    ) : (
                      <div className="px-4 py-3 bg-green-50 text-green-600 rounded-2xl font-black text-[10px] uppercase flex items-center gap-2 border border-green-100">
                        <SafeIcon icon={FiCheckCircle} /> Success
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showSettings && (
          <div className="w-full lg:w-[400px]">
            <PaymentAutomationSettings onSave={() => setShowSettings(false)} />
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentReminders;